import React from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';

import {
  Spotlight,
  SpotlightTarget,
  SpotlightTransition,
  SpotlightPulse,
} from '@atlaskit/onboarding';
import { N0 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import OnboardingImage from './assets/dark-theme-onboarding.svg';
import messages from './theme-selector.i18n';
import {
  ThemeSpotlightState,
  ThemeSelectorSpotlightItemProps,
} from './theme-selector.types';

export const THEME_ONBOARDING_VIEWED_KEY = 'theme-selector-onboarding-viewed';

export const THEME_SPOTLIGHT_TARGET = {
  INTRO: 'themeSelectorIntro',
  DEMO: 'themeSelectorDemo',
};

const DIALOG_WIDTH = 275;

/* eslint @typescript-eslint/ban-types: "warn" */
export const ThemeSelectorSpotlightItem: React.FC<
  ThemeSelectorSpotlightItemProps
> = ({ children, name, isActive }) => {
  if (!isActive) {
    return <>{children}</>;
  }
  return (
    <SpotlightTarget name={name}>
      <SpotlightPulse pulse={false}>{children}</SpotlightPulse>
    </SpotlightTarget>
  );
};

const BaseThemeSelectorSpotlight = ({
  activeSpotlight,
  next,
  end,
  back,
  intl,
}: ThemeSpotlightState & { intl: InjectedIntl }) => {
  const renderActiveSpotlight = () => {
    const spotlights = [
      <Spotlight
        actionsBeforeElement="1/2"
        dialogWidth={DIALOG_WIDTH}
        targetRadius={40}
        actions={[
          {
            onClick: () => next(),
            text: intl.formatMessage(messages.onboardingNextButton),
          },
          {
            onClick: () => end(),
            text: intl.formatMessage(messages.onboardingDismissedButton),
            appearance: 'subtle',
          },
        ]}
        image={OnboardingImage}
        heading={intl.formatMessage(messages.onboardingIntroTitle)}
        target={THEME_SPOTLIGHT_TARGET.INTRO}
        key={THEME_SPOTLIGHT_TARGET.INTRO}
        targetBgColor={token('color.background.neutral', N0)}
      >
        {intl.formatMessage(messages.onboardingIntroDescription)}
      </Spotlight>,
      <Spotlight
        actionsBeforeElement="2/2"
        dialogWidth={DIALOG_WIDTH}
        targetRadius={0}
        actions={[
          {
            onClick: () => end(),
            text: intl.formatMessage(messages.onboardingOkButton),
          },
          {
            onClick: () => back(),
            text: intl.formatMessage(messages.onboardingGoBackButton),
            appearance: 'subtle',
          },
        ]}
        heading={intl.formatMessage(messages.onboardingNextTitle)}
        target={THEME_SPOTLIGHT_TARGET.DEMO}
        key={THEME_SPOTLIGHT_TARGET.DEMO}
        targetBgColor={token('color.background.neutral', N0)}
        dialogPlacement="left top"
      >
        {intl.formatMessage(messages.onboardingNextDescription)}
      </Spotlight>,
    ];

    if (activeSpotlight === null) {
      return null;
    }

    return spotlights[activeSpotlight];
  };

  return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};

export const ThemeSelectorSpotlight = injectIntl(BaseThemeSelectorSpotlight);
